import React from "react";
import {
  Box,
  Typography,
  CardContent,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Button,
  IconButton,
  LinearProgress,
  useTheme,
  alpha
} from "@mui/material";
import {
  ShoppingCart,
  AttachMoney,
  Inventory,
  Star,
  TrendingUp,
  MoreVert,
  Download,
  FilterList,
  CalendarToday,
  Person,
  CheckCircle,
  WatchLater,
  Error
} from "@mui/icons-material";
import ReactApexChart from "react-apexcharts";

const Dashboard = () => {
  const theme = useTheme();

  // ── Stats Data ──
  const vendorStats = {
    totalRevenue: 125430,
    totalOrders: 1847,
    totalProducts: 234,
    avgRating: 4.6,
    growth: 12.5,
    pendingOrders: 23,
    completedOrders: 1824,
    revenueGrowth: 8.3,
    customerSatisfaction: 94
  };

  const statCards = [
    {
      title: "Total Revenue",
      value: `₹${vendorStats.totalRevenue.toLocaleString()}`,
      trend: "+8.3%",
      period: "from last month",
      icon: <TrendingUp sx={{ fontSize: 20 }} />,
      color: "#10b981",
      bgColor: alpha("#10b981", 0.1),
      metric: "₹12.5K this week"
    },
    {
      title: "Total Orders",
      value: vendorStats.totalOrders.toLocaleString(),
      trend: "+12.5%",
      period: "from last month",
      icon: <ShoppingCart sx={{ fontSize: 20 }} />,
      color: "#3b82f6",
      bgColor: alpha("#3b82f6", 0.1),
      metric: "1824 completed"
    },
    {
      title: "Total Products",
      value: vendorStats.totalProducts,
      trend: "+4 Added",
      period: "this week",
      icon: <Inventory sx={{ fontSize: 20 }} />,
      color: "#8b5cf6",
      bgColor: alpha("#8b5cf6", 0.1),
      metric: "23 low stock"
    },
    {
      title: "Avg Rating",
      value: vendorStats.avgRating,
      trend: "Top Rated",
      period: "94% positive",
      icon: <Star sx={{ fontSize: 20 }} />,
      color: "#f59e0b",
      bgColor: alpha("#f59e0b", 0.1),
      metric: "4.8K reviews"
    }
  ];

  // ── Revenue Chart Options ──
  const revenueChartOptions = {
    series: [
      {
        name: "Revenue",
        data: [
          31000,
          35000,
          32000,
          38000,
          42000,
          45000,
          48000,
          52000,
          49000,
          53000,
          58000,
          62000
        ]
      }
    ],
    chart: {
      type: "area",
      height: 280,
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false
        }
      },
      zoom: { enabled: false },
      fontFamily: "Inter, sans-serif",
      background: "transparent"
    },
    dataLabels: { enabled: false },
    stroke: {
      curve: "smooth",
      width: 3,
      colors: ["#10b981"],
      lineCap: "round"
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 0.8,
        opacityFrom: 0.7,
        opacityTo: 0.15,
        stops: [0, 90, 100],
        colorStops: [
          [
            { offset: 0, color: "#10b981", opacity: 0.4 },
            { offset: 100, color: "#10b981", opacity: 0.05 }
          ]
        ]
      }
    },
    xaxis: {
      categories: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec"
      ],
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        style: {
          colors: "#64748b",
          fontFamily: "Inter, sans-serif",
          fontWeight: 500
        }
      },
      tooltip: { enabled: false }
    },
    yaxis: {
      labels: {
        formatter: val => "₹" + (val / 1000).toFixed(0) + "k",
        style: {
          colors: "#64748b",
          fontFamily: "Inter, sans-serif",
          fontWeight: 500
        }
      }
    },
    grid: {
      borderColor: "#e2e8f0",
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } }
    },
    tooltip: {
      theme: "light",
      style: { fontFamily: "Inter, sans-serif" },
      y: {
        formatter: val => "₹" + val.toLocaleString(),
        title: { formatter: () => "Revenue" }
      }
    },
    colors: ["#10b981"],
    markers: {
      size: 4,
      colors: ["#10b981"],
      strokeColors: "#fff",
      strokeWidth: 2,
      hover: { size: 6 }
    }
  };

  // ── Categories Donut Chart Options ──
  const categoriesChartOptions = {
    series: [35, 25, 20, 15, 5],
    chart: {
      type: "donut",
      height: 260,
      fontFamily: "Inter, sans-serif"
    },
    labels: ["Electronics", "Clothing", "Food", "Books", "Others"],
    colors: ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"],
    legend: {
      position: "bottom",
      horizontalAlign: "center",
      labels: {
        colors: "#64748b",
        useSeriesColors: false,
        fontFamily: "Inter, sans-serif"
      }
    },
    plotOptions: {
      pie: {
        donut: {
          size: "70%",
          labels: {
            show: true,
            name: { show: false },
            value: {
              show: true,
              fontFamily: "Inter, sans-serif",
              fontWeight: 700,
              fontSize: "24px",
              color: "#1e293b"
            },
            total: {
              show: true,
              label: "Total Products",
              color: "#64748b",
              fontFamily: "Inter, sans-serif",
              fontWeight: 500,
              formatter: () => vendorStats.totalProducts.toString()
            }
          }
        }
      }
    },
    dataLabels: { enabled: false },
    stroke: { width: 0 },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: { height: 220 },
          legend: { position: "bottom" }
        }
      }
    ]
  };

  // ── Recent Orders Data ──
  const recentOrders = [
    {
      id: "#ORD-001",
      customer: "John Doe",
      product: "MacBook Pro M2",
      amount: 124999,
      status: "completed",
      date: "20 Dec, 2024",
      time: "10:30 AM"
    },
    {
      id: "#ORD-002",
      customer: "Jane Smith",
      product: "iPhone 15 Case",
      amount: 1299,
      status: "pending",
      date: "21 Dec, 2024",
      time: "02:15 PM"
    },
    {
      id: "#ORD-003",
      customer: "Bob Johnson",
      product: "Samsung S24",
      amount: 79999,
      status: "processing",
      date: "22 Dec, 2024",
      time: "11:45 AM"
    },
    {
      id: "#ORD-004",
      customer: "Alice Brown",
      product: "Sony Headphones",
      amount: 24999,
      status: "completed",
      date: "23 Dec, 2024",
      time: "04:20 PM"
    },
    {
      id: "#ORD-005",
      customer: "Charlie Wilson",
      product: "USB-C Cable",
      amount: 499,
      status: "failed",
      date: "24 Dec, 2024",
      time: "09:10 AM"
    }
  ];

  const getStatusConfig = status => {
    const configs = {
      completed: {
        icon: <CheckCircle sx={{ fontSize: 16 }} />,
        color: "#10b981",
        bg: alpha("#10b981", 0.1)
      },
      pending: {
        icon: <WatchLater sx={{ fontSize: 16 }} />,
        color: "#f59e0b",
        bg: alpha("#f59e0b", 0.1)
      },
      processing: {
        icon: <Inventory sx={{ fontSize: 16 }} />,
        color: "#3b82f6",
        bg: alpha("#3b82f6", 0.1)
      },
      failed: {
        icon: <Error sx={{ fontSize: 16 }} />,
        color: "#ef4444",
        bg: alpha("#ef4444", 0.1)
      }
    };
    return configs[status] || configs.pending;
  };

  return (
    <Box
      sx={{
        width: "100%",
        p: { xs: 2, md: 3 },
        backgroundColor: "#f8fafc",
        minHeight: "100vh"
      }}
    >
      {/* Header */}
      <Box sx={{}}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            mb: 2
          }}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontFamily: "Mulish, sans-serif",
                fontWeight: 700,
                color: "#1a1a1a",
                display: "flex",
                alignItems: "center",
                gap: 1
              }}
            >
              Dashboard Overview das.....
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Stats Cards - Glassmorphism + cleaner look */}
      <Box
        sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 2, md: 3 }, mb: 3 }}
      >
        {statCards.map((item, index) =>
          <Paper
            key={index}
            elevation={0}
            sx={{
              flex: "1 1 260px",
              p: 2,
              borderRadius: "20px",
              background: "rgba(255, 255, 255, 0.75)",
              backdropFilter: "blur(12px)",
              border: "1px solid #e2e8f0",
              // boxShadow: "0 8px 32px rgba(31, 38, 135, 0.07)",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-8px)",
                boxShadow: "0 20px 40px rgba(31, 38, 135, 0.12)"
              }
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2.5
                // mb: 2.5
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "16px",
                  background: item.bgColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.6rem"
                }}
              >
                {React.cloneElement(item.icon, { fontSize: "inherit" })}
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#64748b",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.8px",
                    fontSize: "0.75rem",
                    display: "block",
                    mb: 0.5
                  }}
                >
                  {item.title}
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: "#0f172a",
                    lineHeight: 1.1,
                    mb: 0.5
                  }}
                >
                  {item.value}
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}
      </Box>

      {/* Charts Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          gap: 3,
          mb: 5
        }}
      >
        {/* Revenue Chart */}
        <Paper
          elevation={0}
          sx={{
            flex: "3 1 0",
            borderRadius: "16px",
            border: "1px solid #e2e8f0",
            overflow: "hidden"
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 4,
                flexWrap: "wrap",
                gap: 2
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 700,
                    color: "#0f172a",
                    mb: 0.5
                  }}
                >
                  Revenue Analytics
                </Typography>
                <Typography variant="body2" color="#64748b">
                  Monthly revenue performance
                </Typography>
              </Box>
            </Box>

            <ReactApexChart
              options={revenueChartOptions}
              series={revenueChartOptions.series}
              type="area"
              height={300}
            />
          </CardContent>
        </Paper>

        {/* Categories Chart */}
        <Paper
          elevation={0}
          sx={{
            flex: "1 1 0",
            borderRadius: "16px",
            border: "1px solid #e2e8f0",
            overflow: "hidden"
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 700,
                  color: "#0f172a"
                }}
              >
                Sales by Category
              </Typography>
            </Box>

            <ReactApexChart
              options={categoriesChartOptions}
              series={categoriesChartOptions.series}
              type="donut"
              height={280}
            />
          </CardContent>
        </Paper>
      </Box>

      {/* Recent Orders */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "16px",
          border: "1px solid #e2e8f0",
          overflow: "hidden"
        }}
      >
        <Box
          sx={{
            p: 3,
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 700,
                color: "#0f172a",
                mb: 0.5
              }}
            >
              Recent Orders
            </Typography>
            <Typography variant="body2" color="#64748b">
              Latest orders from your store
            </Typography>
          </Box>
          <Button
            variant="text"
            sx={{
              color: "#3b82f6",
              fontWeight: 600,
              textTransform: "none",
              fontFamily: "Inter, sans-serif",
              "&:hover": { backgroundColor: alpha("#3b82f6", 0.1) }
            }}
          >
            View All Orders →
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: "#f8fafc" }}>
              <TableRow>
                {[
                  "ORDER ID",
                  "CUSTOMER",
                  "PRODUCT",
                  "AMOUNT",
                  "STATUS",
                  "DATE & TIME"
                  // ""
                ].map(header =>
                  <TableCell
                    key={header}
                    sx={{
                      color: "#64748b",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      fontFamily: "Inter, sans-serif",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      py: 2,
                      borderBottom: "1px solid #e2e8f0"
                    }}
                  >
                    {header}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {recentOrders.map(order => {
                const statusConfig = getStatusConfig(order.status);
                return (
                  <TableRow
                    key={order.id}
                    hover
                    sx={{
                      "&:hover": { backgroundColor: "#f8fafc" },
                      "&:last-child td": { borderBottom: 0 }
                    }}
                  >
                    <TableCell
                      sx={{
                        fontFamily: "Inter, sans-serif",
                        fontWeight: 600,
                        color: "#0f172a",
                        py: 2.5
                      }}
                    >
                      {order.id}
                    </TableCell>

                    <TableCell sx={{ py: 2.5 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            backgroundColor: alpha("#3b82f6", 0.1),
                            color: "#3b82f6",
                            fontWeight: 600,
                            fontSize: "0.875rem"
                          }}
                        >
                          {order.customer.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography
                            sx={{ fontWeight: 500, fontSize: "0.875rem" }}
                          >
                            {order.customer}
                          </Typography>
                          <Typography
                            sx={{ color: "#64748b", fontSize: "0.75rem" }}
                          >
                            Customer ID: {order.id.slice(1)}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell
                      sx={{
                        fontFamily: "Inter, sans-serif",
                        color: "#475569",
                        py: 2.5,
                        fontWeight: 500
                      }}
                    >
                      {order.product}
                    </TableCell>

                    <TableCell
                      sx={{
                        fontFamily: "Inter, sans-serif",
                        fontWeight: 700,
                        color: "#0f172a",
                        py: 2.5
                      }}
                    >
                      ₹{order.amount.toLocaleString()}
                    </TableCell>

                    <TableCell sx={{ py: 2.5 }}>
                      <Chip
                        icon={statusConfig.icon}
                        label={
                          order.status.charAt(0).toUpperCase() +
                          order.status.slice(1)
                        }
                        size="small"
                        sx={{
                          backgroundColor: statusConfig.bg,
                          color: statusConfig.color,
                          fontWeight: 600,
                          borderRadius: "6px",
                          textTransform: "capitalize",
                          fontFamily: "Inter, sans-serif",
                          height: "28px",
                          "& .MuiChip-icon": { color: statusConfig.color }
                        }}
                      />
                    </TableCell>

                    <TableCell
                      sx={{
                        fontFamily: "Inter, sans-serif",
                        color: "#64748b",
                        py: 2.5
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{ fontSize: "0.875rem", fontWeight: 500 }}
                        >
                          {order.date}
                        </Typography>
                        <Typography
                          sx={{ fontSize: "0.75rem", color: "#94a3b8" }}
                        >
                          {order.time}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* <TableCell sx={{ py: 2.5 }}>
                      <IconButton size="small">
                        <MoreVert sx={{ fontSize: 20, color: "#64748b" }} />
                      </IconButton>
                    </TableCell> */}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default Dashboard;
